import {
  Button,
  DisclosureStep,
  DisclosureStepContent,
  DisclosureStepLabel,
  Listbox,
  Stepper,
  LoadingSpinner,
  Form,
  PostalCodeInputWithValidation,
} from "@talladega/components";
import { ArrowLeftIcon, ArrowRightIcon } from "@talladega/icons";
import DealCustomerTypeEnum from "@common/enums/DealCustomerTypeEnum";
import {
  customerValidation,
  onSubmitError,
  gtmPushData,
} from "@talladega/helpers";
import customerValidationDeprecated from "@common/helpers/validations/customerValidationDeprecated";
import { usePhoneNumberMask } from "@talladega/hooks";
import { Address } from "@common/types/Address";
import { ImageWithTranslation } from "@common/types/ImageWithTranslation";
import { LocaleProps } from "@common/types/LocaleProps";
import { getProvinceByAbbr, provinces } from "@talladega/types";
import DealTypeEnum from "@common/enums/DealTypeEnum";
import useDropOffLocationsList from "@modules/drop-off-location/hooks/useDropOffLocationsList";
import DropOffLocation from "@modules/drop-off-location/types/DropOffLocation";
import GoogleMapsWrapper from "@modules/location/helpers/GoogleMapsWrapper";
import VehicleCoverImageFrame from "@modules/purchase/components/VehicleCoverImageFrame";
import PurchaseFrameLayout from "@modules/purchase/components/common/PurchaseFrameLayout";
import CustomerCancelDropOffAppointmentModal from "@modules/purchase/components/offer/CustomerCancelDropOffAppointmentModal";
import InstantCashOfferLayout from "@modules/purchase/components/offer/InstantCashOfferLayout";
import PickDropOffAppointmentDate from "@modules/purchase/components/offer/PickDropOffAppointmentDate";
import PickDropOffLocation, {
  getClosestLocationForLatLong,
  getClosestLocationForPurchase,
} from "@modules/purchase/components/offer/PickDropOffLocation";
import useGetPurchasesByDealIds from "@modules/purchase/hooks/useGetPurchasesByDealIds";
import useUpdateIcoDetailsMutation from "@modules/purchase/hooks/useUpdateIcoDetailsMutation";
import { Purchase } from "@modules/purchase/types/Purchase";
import {
  getStepById,
  icoCreateAccountStep,
  icoPersonalDetailsStep,
  icoScheduleAppointmentStep,
  isStepAccessible,
} from "@modules/purchase/types/PurchaseStep";
import LoginModalContext from "@modules/user/LoginModalContext";
import useUser from "@modules/user/hooks/useUser";
import {
  createAccountMode,
  loginMode,
} from "@modules/user/types/LoginModalMode";
import { IcoOfferDetail } from "@public/images/instant-cash-offer";
import { Trans, useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import { ReactNode, useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import DeprecatedForm from "@common/components/form";
import { TranslationKeys } from "src/@types/i18next";
import useAssociateTradeInMutation from "@modules/purchase/hooks/useAssociateTradeInMutation";
import useTradeInFromToken from "@modules/trade/hooks/useTradeInFromToken";
import DealCreatedForOfferModal from "@modules/purchase/components/offer/DealCreatedForOfferModal";
import { removeQueryParams } from "@modules/core/routing/routingHelpers";
import useLocalStorage from "@common/hooks/useLocalStorage";
import { PRESERVED_QUERY_PARAMS_KEY } from "@common/hooks/usePreserveQueryParams";
import InstantCashOfferNotFoundModal from "@modules/purchase/components/offer/InstantCashOfferNotFoundModal";
import { ICO_INQUIRY_EMAIL } from "@common/constants";
import HTTPErrorWithBody from "@modules/api/HTTPErrorWithBody";
import { Event as HotjarEvent, hotjar } from "@modules/core/hotjar/types";

const vehicleDefaultImage: ImageWithTranslation = {
  srcEn: IcoOfferDetail,
  srcFr: IcoOfferDetail,
  altKey: "purchase:alt_wty_vehicle_img",
};

type DetailsForm = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  city: string;
  province: string;
  postalCode: string;
  address: string;
  latitude: number | null;
  longitude: number | null;
};

const focusOrder: Array<keyof DetailsForm> = [
  "firstName",
  "lastName",
  "email",
  "phone",
  "address",
  "city",
  "province",
  "postalCode",
];

const IcoConfirmPage = () => {
  const { t } = useTranslation(["purchase", "common"]);
  const router = useRouter();
  const {
    query: { dealId, accountLinked, tradeIn: tradeInToken, source, ...query },
    push,
  } = router;
  const { user } = useUser();
  const { dropOffLocations } = useDropOffLocationsList();

  const validBuyInDealId = user?.deals.find(
    (x) => x.id === dealId && x.type.id === DealTypeEnum.BuyIn
  )?.id;

  const { tradeIn: tradeInFromToken } = useTradeInFromToken({
    token: tradeInToken as string | null,
    source: source as string | null,
    enabled: !validBuyInDealId,
  });

  const [showAlreadyCreatedModal, setShowAlreadyCreatedModal] = useState(false);
  const [showAlreadyAssociatedModal, setShowAlreadyAssociatedModal] =
    useState(false);

  useEffect(() => {
    // if the deal is already created but we don't have the deal id, that means
    // we're not allowed to see it
    if (tradeInFromToken?.isDealAlreadyCreated && !tradeInFromToken.dealId) {
      if (tradeInFromToken.isDealAlreadyAssociated) {
        setShowAlreadyAssociatedModal(true);
      } else {
        setShowAlreadyCreatedModal(true);
      }
    }
  }, [
    tradeInFromToken?.dealId,
    tradeInFromToken?.isDealAlreadyCreated,
    tradeInFromToken?.isDealAlreadyAssociated,
  ]);

  const dealIdToQuery = validBuyInDealId ?? tradeInFromToken?.dealId;

  const { activePurchases } = useGetPurchasesByDealIds(
    dealIdToQuery ? [dealIdToQuery] : []
  );

  const purchase = activePurchases[0] as Purchase | undefined;

  const tradeIn =
    purchase?.tradeIns && purchase.tradeIns.length > 0
      ? purchase.tradeIns[0]
      : null;

  const purchaseDropOffLocation =
    dropOffLocations.find(
      (x) => x.id === purchase?.dropOffDetails?.dropOffLocationId
    ) ?? undefined;

  const dealerDropOffLocation = tradeIn?.originalDealerId
    ? dropOffLocations.find((dol) => dol.dealerId === tradeIn.originalDealerId)
    : null;

  const showDropOffLocationDropdown = !dealerDropOffLocation;

  const [activeStep, setActiveStep] = useState(icoPersonalDetailsStep);

  const mainApplicant = purchase?.customers.find(
    (c) => c.typeId === DealCustomerTypeEnum.MainApplicant
  );

  const mainApplicantAddress = mainApplicant?.currentAddress;

  const { maskPipe: phoneNumberMaskPipe } = usePhoneNumberMask();
  const [selectedDropOffLocation, setSelectedDropOffLocation] =
    useState<DropOffLocation | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    control,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useForm<DetailsForm>({
    values: {
      firstName: mainApplicant?.firstName ?? tradeInFromToken?.firstName ?? "",
      lastName: mainApplicant?.lastName ?? tradeInFromToken?.lastName ?? "",
      email: mainApplicant?.email ?? tradeInFromToken?.email ?? "",
      phone: phoneNumberMaskPipe(
        mainApplicant?.phone ?? tradeInFromToken?.phone ?? ""
      ),
      city: mainApplicantAddress?.city ?? "",
      province: mainApplicantAddress?.provinceAbbr ?? "",
      postalCode: mainApplicantAddress?.postalCode ?? "",
      address: mainApplicantAddress?.fullAddress ?? "",
      latitude: mainApplicantAddress?.latitude ?? null,
      longitude: mainApplicantAddress?.longitude ?? null,
    },
  });

  const updateIcoDetailsMutation = useUpdateIcoDetailsMutation();
  const associateTradeInMutation = useAssociateTradeInMutation();

  const onSubmitDetails = (formInput: DetailsForm) => {
    if (!purchase) {
      // if we got here without a purchase we have to make sure it gets associated first
      associateTradeInMutation.mutate(
        {
          tradeInToken: tradeInToken as string,
          source: source as string,
        },
        {
          onSuccess: (response) => {
            // then we can update its details
            updateIcoDetailsMutation.mutate(
              {
                dealId: response.dealId,
                ...formInput,
              },
              {
                onSuccess: () => {
                  if (dealerDropOffLocation) {
                    setSelectedDropOffLocation(dealerDropOffLocation);
                  } else if (formInput.latitude && formInput.longitude) {
                    const location = getClosestLocationForLatLong(
                      dropOffLocations,
                      formInput.latitude,
                      formInput.longitude
                    );
                    setSelectedDropOffLocation(location || null);
                  } else {
                    setSelectedDropOffLocation(null);
                  }
                  setActiveStep(icoScheduleAppointmentStep);
                },
              }
            );
          },
          onError: (error) => {
            if (error instanceof HTTPErrorWithBody) {
              const err = error as HTTPErrorWithBody;

              if (
                err.response.status === 400 &&
                err.responseJson ===
                  "Trade-in already associated with deal by admin"
              ) {
                gtmPushData({
                  event: "ICO In Progress Modal",
                  description: "ICO In Progress Modal Open",
                  element: "ICO Landing Page",
                });
                hotjar.event(HotjarEvent.IcoInProgressModalOpen);

                setShowAlreadyCreatedModal(true);
              } else {
                setShowAlreadyAssociatedModal(true);
              }
            }
          },
        }
      );
    } else {
      updateIcoDetailsMutation.mutate(
        {
          dealId: purchase.id,
          ...formInput,
        },
        {
          onSuccess: () => {
            if (dealerDropOffLocation) {
              setSelectedDropOffLocation(dealerDropOffLocation);
            } else if (formInput.latitude && formInput.longitude) {
              const location = getClosestLocationForLatLong(
                dropOffLocations,
                formInput.latitude,
                formInput.longitude
              );
              setSelectedDropOffLocation(location || null);
            } else {
              setSelectedDropOffLocation(null);
            }
            setActiveStep(icoScheduleAppointmentStep);
          },
        }
      );
    }
  };

  const [appointmentSchedulingState, setAppointmentSchedulingState] = useState<
    "location" | "time"
  >("location");

  const onAutoCompleteChange = (address: Address | string) => {
    if (typeof address !== "string") {
      reset({
        city: address.city,
        province: address.provinceAbbr,
        postalCode: address.postalCode,
        address: address.address1,
        latitude: address.latitude,
        longitude: address.longitude,
      });
    } else {
      setValue("address", address);
      // reset latitude and longitude if free form address is entered
      setValue("latitude", null);
      setValue("longitude", null);
    }
    setAppointmentSchedulingState("location");
  };

  const onClearAddressForm = () => {
    reset({
      address: "",
      city: "",
      province: "",
      postalCode: "",
      latitude: null,
      longitude: null,
    });
    setAppointmentSchedulingState("location");
  };

  const onAppointmentConfirmed = () => {
    setActiveStep(icoCreateAccountStep);
  };

  const {
    setShowModal: setShowLoginModal,
    modeState: [, setLoginModalModeState],
    setDefaultEmailAddress,
    setDefaultFirstName,
    setDefaultLastName,
  } = useContext(LoginModalContext);

  const onCreateAccountClick = () => {
    const email = getValues("email") ?? "";
    const firstName = getValues("firstName") ?? "";
    const lastName = getValues("lastName") ?? "";

    setDefaultEmailAddress?.(email);
    setDefaultFirstName?.(firstName);
    setDefaultLastName?.(lastName);
    setShowLoginModal(true);
    setLoginModalModeState(createAccountMode);
  };

  const onLoginClick = () => {
    setDefaultEmailAddress?.(getValues("email") ?? "");
    setShowLoginModal(true);
    setLoginModalModeState(loginMode);
  };

  const handleOnDropOffLocationChange = (dropOffLocation: DropOffLocation) => {
    setSelectedDropOffLocation(dropOffLocation);
  };

  const currentPurchaseStep =
    purchase?.currentStepId !== undefined && purchase.currentStepId !== null
      ? getStepById("ico", purchase.currentStepId)
      : null;

  const [isFirstLoad, setIsFirstLoad] = useState(true);

  useEffect(() => {
    if (!purchase?.currentStepId || !isFirstLoad) return;

    setIsFirstLoad(false);

    const currentStep = getStepById("ico", purchase.currentStepId);

    if (!currentStep) return;

    setActiveStep(currentStep);
  }, [isFirstLoad, phoneNumberMaskPipe, purchase, reset]);

  useEffect(() => {
    if (
      user?.isLoggedIn &&
      purchase?.currentStepId === icoCreateAccountStep.id &&
      !accountLinked
    ) {
      push(
        {
          pathname: "/offer-detail",
          query: { dealId: purchase.id },
        },
        undefined,
        { shallow: true }
      );
    }
  }, [
    accountLinked,
    purchase?.currentStepId,
    purchase?.id,
    push,
    user?.isLoggedIn,
  ]);

  useEffect(() => {
    if (!purchase) return;
    let location = null;

    if (purchaseDropOffLocation) {
      location = purchaseDropOffLocation;
    } else if (dealerDropOffLocation) {
      location = dealerDropOffLocation;
    } else {
      location = getClosestLocationForPurchase(dropOffLocations, purchase);
    }

    setSelectedDropOffLocation(location || null);
  }, [
    dealerDropOffLocation,
    dropOffLocations,
    purchase,
    purchaseDropOffLocation,
  ]);

  const [showCancelAppointmentModal, setShowCancelAppointmentModal] =
    useState(false);

  const selectedProvince = watch("province");

  const [, setPreservedQueryParams] = useLocalStorage<Record<string, string>>(
    PRESERVED_QUERY_PARAMS_KEY
  );

  const handleDealCreatedModalClose = () => {
    // remove tradeIn as it's invalid for the current user anyway
    setPreservedQueryParams((prev) => {
      const { tradeIn: _, ...others } = prev;
      return others;
    });
    removeQueryParams(router, ["tradeIn"]);
    push(
      {
        pathname: "/",
        query: { dealId, accountLinked, source, ...query },
      },
      undefined,
      {
        shallow: true,
      }
    );
    setShowAlreadyCreatedModal(false);
  };

  return (
    <>
      <Head>
        <title>{t("purchase:your_cash_offer_checklist_throttle")}</title>
      </Head>
      <PurchaseFrameLayout
        overlayStyle="ico"
        image={
          <VehicleCoverImageFrame vehicleDefaultImage={vehicleDefaultImage} />
        }
        body={
          <div>
            <h2 className="text-primary font-semibold text-5xl mb-6">
              {t("purchase:just_need_some_final_details")}
            </h2>
            <p className="text-secondary font-normal text-xl">
              {t("purchase:please_confirm_personal_information")}
            </p>
          </div>
        }
        frameContent={
          <>
            {user?.isLoggedIn && (
              <Button
                fill="link"
                className="mb-4"
                spacing="tight-hug"
                leftIcon={<ArrowLeftIcon />}
                onClick={() => {
                  push(
                    {
                      pathname: "/offer-detail",
                      query: { ...query },
                    },
                    undefined,
                    { shallow: true }
                  );
                }}
              >
                {t("purchase:back_to_my_ico")}
              </Button>
            )}
            <Stepper activeStep={activeStep.order} stepsLength={3} linear>
              <div className="flex flex-col gap-5">
                <DisclosureStep index={icoPersonalDetailsStep.order}>
                  <DisclosureStepLabel
                    stepLabel={t("common:step_number", { number: 1 })}
                    header={t("purchase:confirm_your_details")}
                    onClick={() => {
                      if (
                        currentPurchaseStep &&
                        isStepAccessible(
                          currentPurchaseStep,
                          icoPersonalDetailsStep
                        )
                      ) {
                        setActiveStep(icoPersonalDetailsStep);
                      }
                    }}
                  />
                  <DisclosureStepContent>
                    <LoadingSpinner
                      isLoading={
                        updateIcoDetailsMutation.isPending ||
                        associateTradeInMutation.isPending
                      }
                      isOverlay
                    />
                    <form
                      onSubmit={handleSubmit(
                        onSubmitDetails,
                        onSubmitError(focusOrder)
                      )}
                    >
                      <Form.Input
                        label={t("common:first_name")}
                        status={errors.firstName ? "error" : "default"}
                        feedback={errors.firstName?.message}
                        maxLength={
                          customerValidationDeprecated.firstName.maxLength
                        }
                        {...register("firstName", {
                          required: t("common:required"),
                          validate:
                            customerValidationDeprecated.firstName.valid,
                          maxLength:
                            customerValidationDeprecated.firstName.maxLength,
                        })}
                      />
                      <p className="text-left text-sm text-secondary font-normal mt-1 mb-5">
                        {t("common:enter_name_as_displayed_on_license")}
                      </p>
                      <Form.Input
                        label={t("common:last_name")}
                        status={errors.lastName ? "error" : "default"}
                        feedback={errors.lastName?.message}
                        maxLength={
                          customerValidationDeprecated.lastName.maxLength
                        }
                        {...register("lastName", {
                          required: t("common:required"),
                          validate: customerValidationDeprecated.lastName.valid,
                          maxLength:
                            customerValidationDeprecated.lastName.maxLength,
                        })}
                      />

                      <div className="grid grid-cols-2 gap-3 mt-5">
                        <Form.Input
                          label={t("common:email")}
                          status={errors.email ? "error" : "default"}
                          feedback={errors.email?.message}
                          maxLength={
                            customerValidationDeprecated.email.maxLength
                          }
                          {...register("email", {
                            required: t("common:required"),
                            validate: customerValidationDeprecated.email.valid,
                            maxLength:
                              customerValidationDeprecated.email.maxLength,
                          })}
                        />
                        <Form.Phone
                          label={t("common:phone_number")}
                          fieldError={errors.phone}
                          {...register("phone", customerValidation.phoneNumber)}
                        />
                      </div>
                      <div className="mt-5">
                        <GoogleMapsWrapper>
                          <DeprecatedForm.AddressAutoComplete
                            fieldError={errors.address}
                            label={t("common:street_address")}
                            placeholder={t("common:search_address")}
                            defaultValue={mainApplicantAddress?.fullAddress}
                            onAddressSelect={onAutoCompleteChange}
                            onAddressClear={onClearAddressForm}
                            lookupType="address"
                            showShortAddress
                            {...register("address", {
                              maxLength:
                                customerValidationDeprecated.streetAddress
                                  .maxLength,
                              required: {
                                value: true,
                                message: t("common:street_address_required"),
                              },
                            })}
                          />
                        </GoogleMapsWrapper>
                      </div>
                      <div className="grid grid-cols-2 gap-3 my-5">
                        <Form.Input
                          label={t("common:city")}
                          status={errors.city ? "error" : "default"}
                          feedback={errors.city?.message}
                          {...register("city", {
                            required: t("common:required"),
                          })}
                        />
                        <Controller
                          control={control}
                          rules={{
                            required: t("common:required"),
                          }}
                          name="province"
                          render={({ field: { onChange, value, ref } }) => (
                            <Listbox
                              items={provinces}
                              label={t("common:province")}
                              keyOption={(item) => item.abbr}
                              displayOption={(item) =>
                                item?.name != null
                                  ? t(item.name as TranslationKeys["common"])
                                  : ""
                              }
                              displayButton={(item) =>
                                item?.name != null
                                  ? t(item.name as TranslationKeys["common"])
                                  : t("common:select_province")
                              }
                              valueOption={(item) => item}
                              status={errors.province ? "error" : "default"}
                              feedback={errors.province?.message}
                              onChange={(province) => {
                                onChange(province.abbr);
                              }}
                              selectedItem={provinces.find(
                                (p) => p.abbr === value
                              )}
                              defaultButtonClassName="text-slate-400"
                              buttonRef={ref}
                            />
                          )}
                        />
                      </div>
                      <PostalCodeInputWithValidation
                        id="postalCode"
                        className="w-full"
                        feedback={errors?.postalCode?.message}
                        status={errors?.postalCode ? "error" : "default"}
                        register={register}
                        fieldKey="postalCode"
                        provinceToValidate={
                          selectedProvince
                            ? getProvinceByAbbr(selectedProvince)
                            : undefined
                        }
                        required
                      />
                      <Button
                        type="submit"
                        className="mt-4"
                        rightIcon={<ArrowRightIcon />}
                        disabled={
                          updateIcoDetailsMutation.isPending ||
                          associateTradeInMutation.isPending
                        }
                      >
                        {t("common:next")}
                      </Button>
                    </form>
                  </DisclosureStepContent>
                </DisclosureStep>

                <DisclosureStep index={icoScheduleAppointmentStep.order}>
                  <DisclosureStepLabel
                    stepLabel={t("common:step_number", { number: 2 })}
                    header={t("purchase:book_your_appointment")}
                    onClick={() => {
                      if (
                        currentPurchaseStep &&
                        isStepAccessible(
                          currentPurchaseStep,
                          icoScheduleAppointmentStep
                        )
                      ) {
                        setActiveStep(icoScheduleAppointmentStep);
                      }
                    }}
                  />
                  <DisclosureStepContent>
                    {dropOffLocations && (
                      <>
                        {appointmentSchedulingState === "location" && (
                          <>
                            <Button
                              leftIcon={<ArrowLeftIcon />}
                              className="my-3"
                              fill="link"
                              spacing="tight-hug"
                              onClick={() =>
                                setActiveStep(icoPersonalDetailsStep)
                              }
                            >
                              {t("purchase:back_to_details")}
                            </Button>
                            <PickDropOffLocation
                              dropOffLocations={dropOffLocations}
                              onNextClick={(location) => {
                                setSelectedDropOffLocation(location);
                                setAppointmentSchedulingState("time");
                              }}
                              defaultDropOffLocation={
                                selectedDropOffLocation || undefined
                              }
                              onDropOffLocationChange={
                                handleOnDropOffLocationChange
                              }
                              showDropdown={showDropOffLocationDropdown}
                              purchaseCustomerAddress={mainApplicantAddress}
                            />
                          </>
                        )}
                        {appointmentSchedulingState === "time" && purchase && (
                          <>
                            <Button
                              leftIcon={<ArrowLeftIcon />}
                              className="my-3"
                              spacing="tight-hug"
                              fill="link"
                              onClick={() =>
                                setAppointmentSchedulingState("location")
                              }
                            >
                              {t("common:select_location")}
                            </Button>
                            <PickDropOffAppointmentDate
                              purchase={purchase}
                              dropOffLocation={
                                selectedDropOffLocation || undefined
                              }
                              onAppointmentConfirmed={onAppointmentConfirmed}
                              isLoading={false}
                            />
                          </>
                        )}
                        {purchase?.dropOffDetails
                          ?.dropOffLocationAppointmentAt && (
                          <div className="mt-6">
                            <Button
                              buttonStyle="danger"
                              fill="link"
                              spacing="no-padding"
                              className="mr-6 mb-3"
                              onClick={() =>
                                setShowCancelAppointmentModal(true)
                              }
                            >
                              {t("common:cancel")}
                            </Button>
                            {appointmentSchedulingState === "time" && (
                              <Button
                                buttonStyle="primary"
                                rightIcon={<ArrowRightIcon />}
                                onClick={() => {
                                  setActiveStep(icoCreateAccountStep);
                                }}
                              >
                                {t("common:next")}
                              </Button>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </DisclosureStepContent>
                </DisclosureStep>

                <DisclosureStep index={icoCreateAccountStep.order}>
                  <DisclosureStepLabel
                    stepLabel={t("common:step_number", { number: 3 })}
                    header={t("common:account_setup")}
                    onClick={() => {
                      if (
                        currentPurchaseStep &&
                        isStepAccessible(
                          currentPurchaseStep,
                          icoCreateAccountStep
                        )
                      ) {
                        setActiveStep(icoCreateAccountStep);
                      }
                    }}
                  />
                  <DisclosureStepContent>
                    <div>
                      <Button
                        leftIcon={<ArrowLeftIcon />}
                        spacing="tight-hug"
                        fill="link"
                        className="my-3"
                        onClick={() =>
                          setActiveStep(icoScheduleAppointmentStep)
                        }
                      >
                        {t("common:select_location")}
                      </Button>
                      {!user?.isLoggedIn && (
                        <>
                          <p>{t("purchase:well_just_need_you")}</p>
                          <div className="mt-6">
                            <Button
                              buttonStyle="primary"
                              fill="filled"
                              onClick={onCreateAccountClick}
                              size="large"
                              spacing="hug"
                              rightIcon={<ArrowRightIcon />}
                            >
                              {t("common:create_an_account")}
                            </Button>
                          </div>
                          <div className="mt-4">
                            <Button
                              buttonStyle="borderless"
                              fill="link"
                              spacing="no-padding"
                              className="text-primary-bold underline hover:no-underline"
                              onClick={onLoginClick}
                            >
                              {t("purchase:log_in_to_an_existing_account")}
                            </Button>
                          </div>
                        </>
                      )}
                      {user?.isLoggedIn && purchase && (
                        <>
                          <h5 className="mb-3">
                            {t("common:thanks_for_logging_in")}
                          </h5>
                          <p className="font-normal text-secondary">
                            <Trans
                              t={t}
                              i18nKey="purchase:profile_resume_where_left_off_message"
                              components={{
                                a: (
                                  <Link
                                    href="/profile/my-offers"
                                    className="text-primary-bold underline"
                                  />
                                ),
                              }}
                            />
                          </p>
                          <Button
                            buttonStyle="primary"
                            className="mt-6"
                            rightIcon={<ArrowRightIcon />}
                            onClick={() => {
                              push(
                                {
                                  pathname: "/offer-detail",
                                  query: { dealId: purchase.id },
                                },
                                undefined,
                                { shallow: true }
                              );
                            }}
                          >
                            {t("common:next")}
                          </Button>
                        </>
                      )}
                    </div>
                  </DisclosureStepContent>
                </DisclosureStep>
              </div>
            </Stepper>
          </>
        }
      />
      {purchase && showCancelAppointmentModal && (
        <CustomerCancelDropOffAppointmentModal
          purchase={purchase}
          onClose={(appointmentCancelled) => {
            if (appointmentCancelled) {
              setAppointmentSchedulingState("location");
            }
            setShowCancelAppointmentModal(false);
          }}
        />
      )}
      <DealCreatedForOfferModal
        isOpen={showAlreadyCreatedModal}
        onClose={handleDealCreatedModalClose}
      />
      <InstantCashOfferNotFoundModal
        showErrorModal={showAlreadyAssociatedModal}
        setShowErrorModal={setShowAlreadyAssociatedModal}
        onPrimaryClick={() =>
          window?.open(`mailto:${ICO_INQUIRY_EMAIL}`, "_blank")
        }
        onSecondaryClick={() => setShowAlreadyAssociatedModal(false)}
      />
    </>
  );
};

IcoConfirmPage.pageName = "IcoConfirmPage";
IcoConfirmPage.getLayout = (page: ReactNode) => (
  <InstantCashOfferLayout removePadding showPurchaseInfo={false}>
    {page}
  </InstantCashOfferLayout>
);

export async function getStaticProps({ locale }: LocaleProps) {
  return {
    props: {
      ...(await serverSideTranslations(locale, [
        "common",
        "common-components",
        "purchase",
      ])),
    },
  };
}

export default IcoConfirmPage;
